import { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Shield, Users, Crown, UserPlus, Settings, 
  CheckCircle2, XCircle, Clock, AlertTriangle,
  Eye, Edit2, Trash2, Send, MessageSquare, 
  FileText, DollarSign, BarChart3, Lock, Unlock,
  ChevronRight, Search, Filter, RefreshCw,
  Star, Award, Zap, Mail, UserCheck, UserX,
  ClipboardList, History, Plus, Check, X, Phone,
  MailPlus, UserCog, AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Permission category icons
const CATEGORY_ICONS = {
  user_management: Users,
  soldier_management: Shield,
  appointment_management: Clock,
  content_management: FileText,
  support_messaging: MessageSquare,
  reports_analytics: BarChart3,
  financial_records: DollarSign,
  system_settings: Settings,
  admin_management: Crown
};

export default function AdminTeamManagement() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('admins');
  
  // Data states
  const [admins, setAdmins] = useState([]);
  const [superSoldiers, setSuperSoldiers] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [pendingSignups, setPendingSignups] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [permissionCategories, setPermissionCategories] = useState({});
  const [superSoldierDashboard, setSuperSoldierDashboard] = useState(null);
  
  // Dialog states
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [showCreateSuperSoldier, setShowCreateSuperSoldier] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showAuditDialog, setShowAuditDialog] = useState(false);
  const [showManualSignup, setShowManualSignup] = useState(false);
  const [selectedSoldier, setSelectedSoldier] = useState(null);
  const [auditData, setAuditData] = useState(null);
  
  // Form states
  const [newAdmin, setNewAdmin] = useState({
    email: '',
    name: '',
    permissions: [],
    can_create_admins: false,
    max_admin_creations: 0
  });
  const [newSuperSoldier, setNewSuperSoldier] = useState({
    user_email: '',
    name: '',
    notes: ''
  });
  const [invite, setInvite] = useState({
    invite_type: 'stylist',
    email: '',
    name: '',
    message: ''
  });
  const [manualSignup, setManualSignup] = useState({
    name: '',
    email: '',
    phone: '',
    soldier_type: 'soldier',
    salon_name: '',
    notes: ''
  });
  const [internalNote, setInternalNote] = useState('');
  const [userHierarchy, setUserHierarchy] = useState(null);
  
  // Check if super admin
  const isSuperAdmin = user?.email?.toLowerCase() === 'georgebroughton71@gmail.com';
  
  // Permission checks for admins
  const hasPermission = (permission) => {
    if (isSuperAdmin) return true;
    if (!userHierarchy?.permissions) return false;
    return userHierarchy.permissions.includes(permission) || userHierarchy.permissions.includes('all');
  };
  
  // Tab access configuration
  const tabAccessConfig = {
    'admins': { 
      requiredPermission: 'view_users', 
      superAdminOnly: false,
      label: 'Admins',
      icon: Users,
      description: 'Manage administrator accounts'
    },
    'super-soldiers': { 
      requiredPermission: 'manage_soldiers', 
      superAdminOnly: false,
      label: 'Super Soldiers',
      icon: Star,
      description: 'Manage elite super soldiers (max 4)'
    },
    'manual-signup': { 
      requiredPermission: 'approve_users', 
      superAdminOnly: true,
      label: 'Manual Signup',
      icon: MailPlus,
      description: 'Create soldier accounts manually'
    },
    'soldier-dashboard': { 
      requiredPermission: 'view_soldier_stats', 
      superAdminOnly: false,
      label: 'All Soldiers',
      icon: Shield,
      description: 'View all soldier statistics'
    },
    'approvals': { 
      requiredPermission: 'approve_users', 
      superAdminOnly: false,
      label: 'Pending Approvals',
      icon: Clock,
      description: 'Review and approve requests'
    },
    'pending-signups': { 
      requiredPermission: 'view_users', 
      superAdminOnly: true,
      label: 'Pending Signups',
      icon: Mail,
      description: 'Track invitation status'
    },
    'audit-logs': { 
      requiredPermission: 'view_reports', 
      superAdminOnly: false,
      label: 'Audit Logs',
      icon: History,
      description: 'View administrative action history'
    }
  };
  
  const canAccessTab = (tabId) => {
    const config = tabAccessConfig[tabId];
    if (!config) return true;
    if (isSuperAdmin) return true;
    if (config.superAdminOnly) return false;
    return hasPermission(config.requiredPermission);
  };

  useEffect(() => {
    if (token) {
      fetchData();
      fetchUserHierarchy();
    }
  }, [token]);
  
  const fetchUserHierarchy = async () => {
    try {
      // For now, we'll determine hierarchy based on the user's admin record
      if (isSuperAdmin) {
        setUserHierarchy({ level: 1, role: 'super_admin', permissions: ['all'] });
      } else {
        // Check if user is in admin_team
        const headers = { Authorization: `Bearer ${token}` };
        const res = await fetch(`${API_URL}/api/admin/team/admins`, { headers });
        if (res.ok) {
          const data = await res.json();
          const currentAdmin = data.admins?.find(a => a.email?.toLowerCase() === user?.email?.toLowerCase());
          if (currentAdmin) {
            setUserHierarchy({ 
              level: 2, 
              role: 'admin', 
              permissions: currentAdmin.permissions || [],
              adminData: currentAdmin
            });
          }
        }
      }
    } catch (err) {
      console.error('Error fetching user hierarchy:', err);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch permission categories (no auth needed)
      const catRes = await fetch(`${API_URL}/api/admin/team/permissions/categories`);
      const catData = await catRes.json();
      setPermissionCategories(catData.categories || {});
      
      // Fetch admins
      const adminsRes = await fetch(`${API_URL}/api/admin/team/admins`, { headers });
      if (adminsRes.ok) {
        const adminsData = await adminsRes.json();
        setAdmins(adminsData.admins || []);
      }
      
      // Fetch super soldiers
      const ssRes = await fetch(`${API_URL}/api/admin/team/super-soldiers`, { headers });
      if (ssRes.ok) {
        const ssData = await ssRes.json();
        setSuperSoldiers(ssData.super_soldiers || []);
      }
      
      // Fetch pending approvals
      const appRes = await fetch(`${API_URL}/api/admin/team/approvals/pending`, { headers });
      if (appRes.ok) {
        const appData = await appRes.json();
        setPendingApprovals(appData.pending_approvals || []);
      }
      
      // Fetch pending signups
      const signupsRes = await fetch(`${API_URL}/api/admin/team/pending-signups`, { headers });
      if (signupsRes.ok) {
        const signupsData = await signupsRes.json();
        setPendingSignups(signupsData.pending_signups || []);
      }
      
      // Fetch super soldier dashboard
      const dashRes = await fetch(`${API_URL}/api/admin/team/super-soldier/dashboard`, { headers });
      if (dashRes.ok) {
        const dashData = await dashRes.json();
        setSuperSoldierDashboard(dashData);
      }
      
      // Fetch audit logs
      const logsRes = await fetch(`${API_URL}/api/admin/team/audit-logs?limit=50`, { headers });
      if (logsRes.ok) {
        const logsData = await logsRes.json();
        setAuditLogs(logsData.audit_logs || []);
      }
    } catch (err) {
      console.error('Error fetching admin team data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAdmin = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/team/admins`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newAdmin)
      });
      
      const data = await res.json();
      
      if (res.ok) {
        if (data.requires_approval) {
          alert('Admin creation request submitted. Pending Super Admin approval.');
        } else {
          alert('Admin created successfully!');
        }
        setShowCreateAdmin(false);
        setNewAdmin({ email: '', name: '', permissions: [], can_create_admins: false, max_admin_creations: 0 });
        fetchData();
      } else {
        alert(data.detail || 'Failed to create admin');
      }
    } catch (err) {
      console.error('Error creating admin:', err);
      alert('Failed to create admin');
    }
  };

  const handleCreateSuperSoldier = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/team/super-soldiers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newSuperSoldier)
      });
      
      const data = await res.json();
      
      if (res.ok) {
        if (data.requires_approval) {
          alert('Super Soldier assignment request submitted. Pending Super Admin approval.');
        } else {
          alert('Super Soldier assigned successfully!');
        }
        setShowCreateSuperSoldier(false);
        setNewSuperSoldier({ user_email: '', name: '', notes: '' });
        fetchData();
      } else {
        alert(data.detail || 'Failed to assign Super Soldier');
      }
    } catch (err) {
      console.error('Error creating super soldier:', err);
      alert('Failed to assign Super Soldier');
    }
  };

  const handleApproval = async (approvalId, action) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/team/approvals/${approvalId}/action`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ action, notes: '' })
      });
      
      if (res.ok) {
        alert(`Request ${action}d successfully!`);
        fetchData();
      } else {
        const data = await res.json();
        alert(data.detail || `Failed to ${action} request`);
      }
    } catch (err) {
      console.error('Error processing approval:', err);
    }
  };

  const handleRevokeAdmin = async (adminId) => {
    if (!confirm('Are you sure you want to revoke this admin\'s privileges?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/team/admins/${adminId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await res.json();
      
      if (res.ok) {
        if (data.requires_approval) {
          alert('Revocation request submitted. Pending Super Admin approval.');
        } else {
          alert('Admin privileges revoked successfully!');
        }
        fetchData();
      } else {
        alert(data.detail || 'Failed to revoke admin');
      }
    } catch (err) {
      console.error('Error revoking admin:', err);
    }
  };

  const handleManualSignup = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/team/manual-signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(manualSignup)
      });
      
      const data = await res.json();
      
      if (res.ok) {
        alert(`Signup email sent to ${manualSignup.email}! They will receive instructions to complete their account.`);
        setShowManualSignup(false);
        setManualSignup({ name: '', email: '', phone: '', soldier_type: 'soldier', salon_name: '', notes: '' });
        fetchData();
      } else {
        alert(data.detail || 'Failed to create signup');
      }
    } catch (err) {
      console.error('Error creating manual signup:', err);
      alert('Failed to create signup');
    }
  };

  const handleResendSignup = async (signupId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/team/pending-signups/${signupId}/resend`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        alert('Signup email resent successfully!');
        fetchData();
      } else {
        const data = await res.json();
        alert(data.detail || 'Failed to resend email');
      }
    } catch (err) {
      console.error('Error resending signup:', err);
    }
  };

  const handleSendInvite = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/team/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(invite)
      });
      
      if (res.ok) {
        alert('Invitation sent successfully!');
        setShowInviteDialog(false);
        setInvite({ invite_type: 'stylist', email: '', name: '', message: '' });
      } else {
        const data = await res.json();
        alert(data.detail || 'Failed to send invitation');
      }
    } catch (err) {
      console.error('Error sending invite:', err);
    }
  };

  const handleAuditSoldier = async (soldierId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/team/super-soldier/audit/${soldierId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setAuditData(data);
        setSelectedSoldier(soldierId);
        setShowAuditDialog(true);
      }
    } catch (err) {
      console.error('Error fetching audit data:', err);
    }
  };

  const handleAddNote = async () => {
    if (!internalNote.trim() || !selectedSoldier) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/team/super-soldier/notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          target_type: 'soldier',
          target_id: selectedSoldier,
          note: internalNote
        })
      });
      
      if (res.ok) {
        setInternalNote('');
        handleAuditSoldier(selectedSoldier); // Refresh audit data
        alert('Note added successfully!');
      }
    } catch (err) {
      console.error('Error adding note:', err);
    }
  };

  const togglePermission = (permission) => {
    setNewAdmin(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Crown className="w-6 h-6 text-amber-400" />
            Admin Team Management
          </h2>
          <p className="text-zinc-400">Manage admins, super soldiers, and permissions</p>
          {/* Role Badge */}
          <div className="flex items-center gap-2 mt-2">
            {isSuperAdmin ? (
              <Badge className="bg-amber-500 text-black">
                <Crown className="w-3 h-3 mr-1" />
                Super Admin
              </Badge>
            ) : userHierarchy?.role === 'admin' ? (
              <Badge className="bg-blue-500 text-white">
                <Users className="w-3 h-3 mr-1" />
                Admin
              </Badge>
            ) : (
              <Badge className="bg-zinc-500 text-white">
                <Users className="w-3 h-3 mr-1" />
                Viewer
              </Badge>
            )}
            {userHierarchy?.permissions && !isSuperAdmin && (
              <span className="text-xs text-zinc-500">
                {userHierarchy.permissions.length} permission{userHierarchy.permissions.length !== 1 ? 's' : ''} assigned
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => { fetchData(); fetchUserHierarchy(); }} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          {(isSuperAdmin || hasPermission('send_messages')) && (
            <Button onClick={() => setShowInviteDialog(true)} className="bg-amber-500 hover:bg-amber-600 text-black">
              <Send className="w-4 h-4 mr-2" />
              Send Invite
            </Button>
          )}
        </div>
      </div>

      {/* Pending Approvals Alert */}
      {pendingApprovals.length > 0 && isSuperAdmin && (
        <Card className="bg-amber-500/10 border-amber-500/30">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              <span className="text-amber-400 font-medium">
                {pendingApprovals.length} pending approval{pendingApprovals.length !== 1 ? 's' : ''} require your attention
              </span>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              className="border-amber-500/50 text-amber-400"
              onClick={() => setActiveTab('approvals')}
            >
              Review Now
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Tabs - Enhanced with lock states */}
      <div className="bg-zinc-900/50 rounded-xl p-4 border border-zinc-800">
        <div className="flex flex-wrap gap-2">
          {Object.entries(tabAccessConfig).map(([tabId, config]) => {
            const isLocked = !canAccessTab(tabId);
            const isActive = activeTab === tabId;
            const TabIcon = config.icon;
            const count = tabId === 'approvals' ? pendingApprovals.length : 
                         tabId === 'pending-signups' ? pendingSignups.length : 0;
            
            return (
              <Button
                key={tabId}
                variant={isActive ? 'default' : 'ghost'}
                size="sm"
                onClick={() => !isLocked && setActiveTab(tabId)}
                disabled={isLocked}
                className={`relative ${
                  isActive 
                    ? 'bg-amber-500 text-black hover:bg-amber-600' 
                    : isLocked 
                      ? 'text-zinc-600 cursor-not-allowed opacity-50' 
                      : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                }`}
                title={isLocked ? `🔒 Requires ${config.superAdminOnly ? 'Super Admin' : config.requiredPermission} permission` : config.description}
              >
                {isLocked && <Lock className="w-3 h-3 mr-1 text-red-400" />}
                <TabIcon className={`w-4 h-4 mr-2 ${isLocked ? 'text-zinc-600' : ''}`} />
                {config.label}
                {count > 0 && !isLocked && (
                  <Badge className="ml-2 bg-red-500 text-white text-xs animate-pulse">{count}</Badge>
                )}
                {isLocked && (
                  <Badge className="ml-2 bg-red-500/20 text-red-400 text-xs">LOCKED</Badge>
                )}
              </Button>
            );
          })}
        </div>
        
        {/* Permission legend for non-super admins */}
        {!isSuperAdmin && userHierarchy && (
          <div className="mt-3 pt-3 border-t border-zinc-800">
            <p className="text-xs text-zinc-500 flex items-center gap-2">
              <Lock className="w-3 h-3" />
              <span>Locked tabs require additional permissions from Super Admin</span>
            </p>
          </div>
        )}
      </div>

      {/* Tab Content */}
      {activeTab === 'admins' && canAccessTab('admins') && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-white">Administrators</h3>
            <Button onClick={() => setShowCreateAdmin(true)} className="bg-green-500 hover:bg-green-600 text-black">
              <UserPlus className="w-4 h-4 mr-2" />
              Add Admin
            </Button>
          </div>
          
          <div className="grid gap-4">
            {admins.map(admin => (
              <Card key={admin.admin_id || admin.email} className="bg-zinc-900 border-zinc-800">
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${admin.is_super_admin ? 'bg-amber-500/20' : 'bg-zinc-800'}`}>
                      {admin.is_super_admin ? (
                        <Crown className="w-6 h-6 text-amber-400" />
                      ) : (
                        <Users className="w-6 h-6 text-zinc-400" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-white">{admin.name}</span>
                        {admin.is_super_admin && (
                          <Badge className="bg-amber-500/20 text-amber-400 text-xs">Super Admin</Badge>
                        )}
                        {!admin.active && (
                          <Badge className="bg-red-500/20 text-red-400 text-xs">Revoked</Badge>
                        )}
                      </div>
                      <p className="text-sm text-zinc-500">{admin.email}</p>
                      {admin.permissions && !admin.is_super_admin && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {admin.permissions.slice(0, 3).map(perm => (
                            <Badge key={perm} variant="outline" className="text-xs border-zinc-700 text-zinc-400">
                              {perm}
                            </Badge>
                          ))}
                          {admin.permissions.length > 3 && (
                            <Badge variant="outline" className="text-xs border-zinc-700 text-zinc-400">
                              +{admin.permissions.length - 3} more
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  {!admin.is_super_admin && admin.active && (
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" className="text-zinc-400">
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-red-400 hover:text-red-300"
                        onClick={() => handleRevokeAdmin(admin.admin_id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'super-soldiers' && canAccessTab('super-soldiers') && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold text-white">Super Soldiers</h3>
              <p className="text-sm text-zinc-500">
                {superSoldiers.length} / 4 slots filled
              </p>
            </div>
            {superSoldiers.length < 4 && (
              <Button onClick={() => setShowCreateSuperSoldier(true)} className="bg-purple-500 hover:bg-purple-600 text-white">
                <Award className="w-4 h-4 mr-2" />
                Assign Super Soldier
              </Button>
            )}
          </div>
          
          <div className="grid gap-4 md:grid-cols-2">
            {superSoldiers.map(ss => (
              <Card key={ss.super_soldier_id} className="bg-zinc-900 border-zinc-800">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-purple-500/20 rounded-full flex items-center justify-center">
                      <Award className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <p className="font-medium text-white">{ss.name}</p>
                      <p className="text-sm text-zinc-500">{ss.email}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {ss.permissions?.map(perm => (
                      <Badge key={perm} variant="outline" className="text-xs border-purple-500/30 text-purple-400">
                        {perm.replace(/_/g, ' ')}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
            
            {/* Empty slots */}
            {Array(4 - superSoldiers.length).fill(0).map((_, idx) => (
              <Card key={`empty-${idx}`} className="bg-zinc-900/50 border-zinc-800 border-dashed">
                <CardContent className="p-4 flex items-center justify-center h-full min-h-[120px]">
                  <p className="text-zinc-600">Available Slot</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'soldier-dashboard' && canAccessTab('soldier-dashboard') && superSoldierDashboard && (
        <div className="space-y-4">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-white">{superSoldierDashboard.totals?.total_soldiers || 0}</p>
                <p className="text-sm text-zinc-500">Total Soldiers</p>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-green-400">{superSoldierDashboard.totals?.total_referrals || 0}</p>
                <p className="text-sm text-zinc-500">Total Referrals</p>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-amber-400">${(superSoldierDashboard.totals?.total_earnings || 0).toFixed(2)}</p>
                <p className="text-sm text-zinc-500">Total Earnings</p>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-purple-400">{superSoldierDashboard.totals?.super_soldier_count || 0}/4</p>
                <p className="text-sm text-zinc-500">Super Soldiers</p>
              </CardContent>
            </Card>
          </div>
          
          {/* Soldiers Table */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white">All Soldiers</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <table className="w-full">
                  <thead className="sticky top-0 bg-zinc-900">
                    <tr className="border-b border-zinc-800">
                      <th className="text-left p-2 text-zinc-400 text-sm">Soldier</th>
                      <th className="text-center p-2 text-zinc-400 text-sm">Referrals</th>
                      <th className="text-center p-2 text-zinc-400 text-sm">Earnings</th>
                      <th className="text-center p-2 text-zinc-400 text-sm">Stripe</th>
                      <th className="text-center p-2 text-zinc-400 text-sm">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {superSoldierDashboard.soldiers?.map(soldier => (
                      <tr key={soldier.soldier_id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                        <td className="p-2">
                          <div>
                            <p className="text-white font-medium">{soldier.name}</p>
                            <p className="text-xs text-zinc-500">{soldier.email}</p>
                            <Badge variant="outline" className="text-xs mt-1 border-amber-500/30 text-amber-400">
                              {soldier.referral_code}
                            </Badge>
                          </div>
                        </td>
                        <td className="p-2 text-center">
                          <span className="text-white font-medium">{soldier.total_referrals}</span>
                          <span className="text-zinc-500 text-sm"> ({soldier.active_referrals} active)</span>
                        </td>
                        <td className="p-2 text-center text-green-400 font-medium">
                          ${soldier.total_earnings?.toFixed(2) || '0.00'}
                        </td>
                        <td className="p-2 text-center">
                          {soldier.stripe_connected ? (
                            <CheckCircle2 className="w-5 h-5 text-green-400 mx-auto" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-400 mx-auto" />
                          )}
                        </td>
                        <td className="p-2 text-center">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleAuditSoldier(soldier.soldier_id)}
                          >
                            <Eye className="w-4 h-4 text-zinc-400" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'approvals' && canAccessTab('approvals') && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">Pending Approvals</h3>
          
          {pendingApprovals.length === 0 ? (
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-8 text-center">
                <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-3" />
                <p className="text-zinc-400">No pending approvals</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {pendingApprovals.map(approval => (
                <Card key={approval.approval_id} className="bg-zinc-900 border-zinc-800">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className="bg-amber-500/20 text-amber-400">
                            {approval.action_type.replace(/_/g, ' ')}
                          </Badge>
                          <span className="text-xs text-zinc-500">
                            {new Date(approval.created_at).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-white font-medium">{approval.description}</p>
                        <p className="text-sm text-zinc-500">Requested by: {approval.requester_email}</p>
                      </div>
                      {isSuperAdmin && (
                        <div className="flex items-center gap-2">
                          <Button 
                            size="sm" 
                            className="bg-green-500 hover:bg-green-600 text-black"
                            onClick={() => handleApproval(approval.approval_id, 'approve')}
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                          <Button 
                            size="sm" 
                            variant="destructive"
                            onClick={() => handleApproval(approval.approval_id, 'reject')}
                          >
                            <X className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Manual Signup Tab */}
      {activeTab === 'manual-signup' && canAccessTab('manual-signup') && (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-white mb-2">Manual Soldier/Super Soldier Signup</h3>
            <p className="text-zinc-400 text-sm">Create a signup for a new soldier or super soldier. They will receive an email to complete their account.</p>
          </div>
          
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <MailPlus className="w-5 h-5 text-amber-400" />
                Create Soldier Account
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300">Full Name *</Label>
                  <Input
                    value={manualSignup.name}
                    onChange={(e) => setManualSignup(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="John Smith"
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Email Address *</Label>
                  <Input
                    type="email"
                    value={manualSignup.email}
                    onChange={(e) => setManualSignup(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="john@example.com"
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300">Phone Number *</Label>
                  <Input
                    value={manualSignup.phone}
                    onChange={(e) => setManualSignup(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="(555) 123-4567"
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Account Type *</Label>
                  <select
                    value={manualSignup.soldier_type}
                    onChange={(e) => setManualSignup(prev => ({ ...prev, soldier_type: e.target.value }))}
                    className="w-full mt-1 p-2 bg-zinc-800 border border-zinc-700 rounded-md text-white"
                  >
                    <option value="soldier">HairChair Soldier</option>
                    <option value="super_soldier">Super Soldier (Limited to 4)</option>
                  </select>
                </div>
              </div>
              
              <div>
                <Label className="text-zinc-300">Business/Salon Name</Label>
                <Input
                  value={manualSignup.salon_name}
                  onChange={(e) => setManualSignup(prev => ({ ...prev, salon_name: e.target.value }))}
                  placeholder="Smith's Barbershop"
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              
              <div>
                <Label className="text-zinc-300">Internal Notes (Optional)</Label>
                <Textarea
                  value={manualSignup.notes}
                  onChange={(e) => setManualSignup(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Why are you adding this person? Any special notes..."
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              
              <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="text-amber-400 font-medium">What happens next?</p>
                    <ul className="text-zinc-400 mt-1 space-y-1">
                      <li>• An email will be sent to the person with a signup link</li>
                      <li>• They will click the link and set their password</li>
                      <li>• Their account will be automatically approved as a Soldier</li>
                      <li>• They can immediately start earning commissions</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <Button 
                onClick={handleManualSignup}
                className="w-full bg-amber-500 hover:bg-amber-600 text-black"
                disabled={!manualSignup.name || !manualSignup.email || !manualSignup.phone}
              >
                <Send className="w-4 h-4 mr-2" />
                Send Signup Email
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Pending Signups Tab */}
      {activeTab === 'pending-signups' && canAccessTab('pending-signups') && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">Pending Signups</h3>
          <p className="text-zinc-400 text-sm">People who have been invited but haven&apos;t completed their signup yet.</p>
          
          {pendingSignups.length === 0 ? (
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-8 text-center">
                <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-3" />
                <p className="text-zinc-400">No pending signups</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {pendingSignups.map(signup => (
                <Card key={signup.signup_id} className="bg-zinc-900 border-zinc-800">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${signup.soldier_type === 'super_soldier' ? 'bg-purple-500/20' : 'bg-amber-500/20'}`}>
                          {signup.soldier_type === 'super_soldier' ? (
                            <Award className="w-5 h-5 text-purple-400" />
                          ) : (
                            <Shield className="w-5 h-5 text-amber-400" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-white">{signup.name}</span>
                            <Badge className={signup.soldier_type === 'super_soldier' ? 'bg-purple-500/20 text-purple-400' : 'bg-amber-500/20 text-amber-400'}>
                              {signup.soldier_type === 'super_soldier' ? 'Super Soldier' : 'Soldier'}
                            </Badge>
                            <Badge className="bg-yellow-500/20 text-yellow-400">
                              <Clock className="w-3 h-3 mr-1" />
                              Pending
                            </Badge>
                          </div>
                          <p className="text-sm text-zinc-500">{signup.email}</p>
                          <p className="text-xs text-zinc-600">
                            Created: {new Date(signup.created_at).toLocaleDateString()} • 
                            Expires: {new Date(signup.expires_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleResendSignup(signup.signup_id)}
                        className="border-amber-500/50 text-amber-400"
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        Resend Email
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'audit-logs' && canAccessTab('audit-logs') && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">Audit Logs</h3>
          
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-0">
              <ScrollArea className="h-[500px]">
                <table className="w-full">
                  <thead className="sticky top-0 bg-zinc-900">
                    <tr className="border-b border-zinc-800">
                      <th className="text-left p-3 text-zinc-400 text-sm">Action</th>
                      <th className="text-left p-3 text-zinc-400 text-sm">Actor</th>
                      <th className="text-left p-3 text-zinc-400 text-sm">Target</th>
                      <th className="text-left p-3 text-zinc-400 text-sm">Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogs.map(log => (
                      <tr key={log.audit_id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                        <td className="p-3">
                          <Badge variant="outline" className="border-zinc-700 text-zinc-300">
                            {log.action.replace(/_/g, ' ')}
                          </Badge>
                        </td>
                        <td className="p-3 text-zinc-400 text-sm">{log.actor_email}</td>
                        <td className="p-3 text-zinc-400 text-sm">{log.target_type}: {log.target_id}</td>
                        <td className="p-3 text-zinc-500 text-sm">
                          {new Date(log.created_at).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Locked Feature Placeholder - Shows when user tries to access a locked tab */}
      {!canAccessTab(activeTab) && (
        <Card className="bg-zinc-900 border-red-500/30">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-red-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Feature Locked</h3>
            <p className="text-zinc-400 mb-4 max-w-md mx-auto">
              {tabAccessConfig[activeTab]?.superAdminOnly 
                ? "This feature is restricted to Super Admin only."
                : `You need the "${tabAccessConfig[activeTab]?.requiredPermission?.replace(/_/g, ' ')}" permission to access this feature.`
              }
            </p>
            <div className="bg-zinc-800/50 rounded-lg p-4 max-w-sm mx-auto">
              <p className="text-sm text-zinc-500">
                <strong className="text-amber-400">Need access?</strong>
                <br />
                Contact the Super Admin to request this permission.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Admin Dialog */}
      <Dialog open={showCreateAdmin} onOpenChange={setShowCreateAdmin}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Create New Admin</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Assign admin privileges with specific permissions (QuickBooks-style)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">Name</Label>
                <Input
                  value={newAdmin.name}
                  onChange={(e) => setNewAdmin(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Admin name"
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Email</Label>
                <Input
                  type="email"
                  value={newAdmin.email}
                  onChange={(e) => setNewAdmin(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="admin@example.com"
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-zinc-300 mb-2 block">Permissions</Label>
              {Object.keys(permissionCategories).length === 0 ? (
                <div className="text-center py-6 bg-zinc-800/50 rounded-lg border border-zinc-700">
                  <RefreshCw className="w-6 h-6 text-amber-400 animate-spin mx-auto mb-2" />
                  <p className="text-zinc-400 text-sm">Loading permission categories...</p>
                </div>
              ) : (
                <div className="grid gap-3 max-h-[400px] overflow-y-auto pr-2">
                  {Object.entries(permissionCategories).map(([key, category]) => {
                    const Icon = CATEGORY_ICONS[key] || Settings;
                    const isSuperAdminOnly = category.super_admin_only;
                    const requiresApproval = category.requires_approval;
                    
                    return (
                      <Card key={key} className={`bg-zinc-800/50 border-zinc-700 ${isSuperAdminOnly ? 'opacity-50' : ''}`}>
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isSuperAdminOnly ? 'bg-red-500/20' : 'bg-amber-500/20'}`}>
                                <Icon className={`w-4 h-4 ${isSuperAdminOnly ? 'text-red-400' : 'text-amber-400'}`} />
                              </div>
                              <div>
                                <span className="font-medium text-white">{category.label}</span>
                                <div className="flex gap-1 mt-0.5">
                                  {isSuperAdminOnly && (
                                    <Badge className="bg-red-500/20 text-red-400 text-xs">
                                      <Lock className="w-2 h-2 mr-1" />
                                      Super Admin Only
                                    </Badge>
                                  )}
                                  {requiresApproval && (
                                    <Badge className="bg-amber-500/20 text-amber-400 text-xs">
                                      <Clock className="w-2 h-2 mr-1" />
                                      Requires Approval
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                            {/* Select All for this category */}
                            {!isSuperAdminOnly && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const allSelected = category.permissions.every(p => newAdmin.permissions.includes(p));
                                  if (allSelected) {
                                    setNewAdmin(prev => ({
                                      ...prev,
                                      permissions: prev.permissions.filter(p => !category.permissions.includes(p))
                                    }));
                                  } else {
                                    setNewAdmin(prev => ({
                                      ...prev,
                                      permissions: [...new Set([...prev.permissions, ...category.permissions])]
                                    }));
                                  }
                                }}
                                className="text-xs text-amber-400 hover:text-amber-300"
                              >
                                {category.permissions.every(p => newAdmin.permissions.includes(p)) ? 'Deselect All' : 'Select All'}
                              </Button>
                            )}
                          </div>
                          <p className="text-xs text-zinc-500 mb-3">{category.description}</p>
                          <div className="flex flex-wrap gap-2">
                            {category.permissions.map(perm => (
                              <label 
                                key={perm}
                                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-colors cursor-pointer ${
                                  isSuperAdminOnly 
                                    ? 'border-zinc-700 bg-zinc-800/30 cursor-not-allowed opacity-50' 
                                    : newAdmin.permissions.includes(perm)
                                      ? 'border-amber-500 bg-amber-500/20 text-amber-300'
                                      : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-600 text-zinc-400'
                                }`}
                              >
                                <Checkbox
                                  checked={newAdmin.permissions.includes(perm)}
                                  onCheckedChange={() => !isSuperAdminOnly && togglePermission(perm)}
                                  disabled={isSuperAdminOnly}
                                  className="data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                                />
                                <span className="text-sm capitalize">{perm.replace(/_/g, ' ')}</span>
                              </label>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
              
              {/* Selected permissions summary */}
              {newAdmin.permissions.length > 0 && (
                <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <p className="text-amber-400 text-sm font-medium mb-1">
                    {newAdmin.permissions.length} permission{newAdmin.permissions.length !== 1 ? 's' : ''} selected
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {newAdmin.permissions.slice(0, 5).map(p => (
                      <Badge key={p} className="bg-amber-500/20 text-amber-300 text-xs">
                        {p.replace(/_/g, ' ')}
                      </Badge>
                    ))}
                    {newAdmin.permissions.length > 5 && (
                      <Badge className="bg-zinc-700 text-zinc-300 text-xs">
                        +{newAdmin.permissions.length - 5} more
                      </Badge>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            {/* Admin Meta-Permissions */}
            <Card className="bg-purple-500/10 border-purple-500/30">
              <CardContent className="p-4">
                <h4 className="text-purple-400 font-medium mb-3 flex items-center gap-2">
                  <Crown className="w-4 h-4" />
                  Admin Meta-Permissions
                </h4>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 p-2 rounded-lg hover:bg-purple-500/10 cursor-pointer">
                    <Checkbox
                      checked={newAdmin.can_create_admins}
                      onCheckedChange={(checked) => setNewAdmin(prev => ({ ...prev, can_create_admins: checked }))}
                      className="data-[state=checked]:bg-purple-500 data-[state=checked]:border-purple-500"
                    />
                    <div>
                      <span className="text-white font-medium">Can create other admins</span>
                      <p className="text-xs text-zinc-500">Allow this admin to create new admin accounts (requires your approval)</p>
                    </div>
                  </label>
                  
                  {newAdmin.can_create_admins && (
                    <div className="ml-8 p-3 bg-zinc-800/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Label className="text-zinc-400 text-sm">Maximum admins they can create:</Label>
                        <Input
                          type="number"
                          min="1"
                          max="10"
                          value={newAdmin.max_admin_creations}
                          onChange={(e) => setNewAdmin(prev => ({ ...prev, max_admin_creations: parseInt(e.target.value) || 1 }))}
                          className="w-20 bg-zinc-800 border-zinc-700 text-center"
                        />
                      </div>
                      <p className="text-xs text-zinc-500 mt-2">
                        Note: Each admin creation will still require Super Admin approval
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
          
          <DialogFooter className="border-t border-zinc-800 pt-4">
            <Button variant="ghost" onClick={() => setShowCreateAdmin(false)}>Cancel</Button>
            <Button 
              onClick={handleCreateAdmin}
              className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white"
              disabled={!newAdmin.email || !newAdmin.name}
            >
              <UserPlus className="w-4 h-4 mr-2" />
              {isSuperAdmin ? 'Create Admin' : 'Submit for Approval'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Super Soldier Dialog */}
      <Dialog open={showCreateSuperSoldier} onOpenChange={setShowCreateSuperSoldier}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white">Assign Super Soldier</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Promote an approved soldier to Super Soldier status
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-zinc-300">Soldier Email</Label>
              <Input
                type="email"
                value={newSuperSoldier.user_email}
                onChange={(e) => setNewSuperSoldier(prev => ({ ...prev, user_email: e.target.value }))}
                placeholder="soldier@example.com"
                className="bg-zinc-800 border-zinc-700"
              />
              <p className="text-xs text-zinc-500 mt-1">Must be an existing approved soldier</p>
            </div>
            <div>
              <Label className="text-zinc-300">Name</Label>
              <Input
                value={newSuperSoldier.name}
                onChange={(e) => setNewSuperSoldier(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Soldier name"
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <Label className="text-zinc-300">Notes (Optional)</Label>
              <Textarea
                value={newSuperSoldier.notes}
                onChange={(e) => setNewSuperSoldier(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Why are they being promoted?"
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreateSuperSoldier(false)}>Cancel</Button>
            <Button 
              onClick={handleCreateSuperSoldier}
              className="bg-purple-500 hover:bg-purple-600 text-white"
              disabled={!newSuperSoldier.user_email || !newSuperSoldier.name}
            >
              {isSuperAdmin ? 'Assign Super Soldier' : 'Submit for Approval'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Invite Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white">Send Platform Invite</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Invite someone to join MyHairChair
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-zinc-300">Invite As</Label>
              <select
                value={invite.invite_type}
                onChange={(e) => setInvite(prev => ({ ...prev, invite_type: e.target.value }))}
                className="w-full mt-1 p-2 bg-zinc-800 border border-zinc-700 rounded-md text-white"
              >
                <option value="client">Client</option>
                <option value="stylist">Stylist</option>
                <option value="barber">Barber</option>
                <option value="soldier">Soldier</option>
              </select>
            </div>
            <div>
              <Label className="text-zinc-300">Name</Label>
              <Input
                value={invite.name}
                onChange={(e) => setInvite(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Recipient name"
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <Label className="text-zinc-300">Email</Label>
              <Input
                type="email"
                value={invite.email}
                onChange={(e) => setInvite(prev => ({ ...prev, email: e.target.value }))}
                placeholder="recipient@example.com"
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <Label className="text-zinc-300">Personal Message (Optional)</Label>
              <Textarea
                value={invite.message}
                onChange={(e) => setInvite(prev => ({ ...prev, message: e.target.value }))}
                placeholder="Add a personal note..."
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowInviteDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleSendInvite}
              className="bg-amber-500 hover:bg-amber-600 text-black"
              disabled={!invite.email || !invite.name}
            >
              <Send className="w-4 h-4 mr-2" />
              Send Invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit Dialog */}
      <Dialog open={showAuditDialog} onOpenChange={setShowAuditDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Soldier Audit</DialogTitle>
            <DialogDescription className="text-zinc-400">
              View-only audit of soldier history (no financial data shown)
            </DialogDescription>
          </DialogHeader>
          
          {auditData && (
            <div className="space-y-4 py-4">
              {/* Soldier Info */}
              <Card className="bg-zinc-800/50 border-zinc-700">
                <CardContent className="p-4">
                  <h4 className="font-medium text-white mb-2">Soldier Information</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-zinc-500">Name:</span> <span className="text-white">{auditData.soldier?.name || auditData.soldier?.owner_name}</span></div>
                    <div><span className="text-zinc-500">Email:</span> <span className="text-white">{auditData.soldier?.email}</span></div>
                    <div><span className="text-zinc-500">Referral Code:</span> <span className="text-amber-400">{auditData.soldier?.referral_code}</span></div>
                    <div><span className="text-zinc-500">Status:</span> <span className="text-green-400">{auditData.soldier?.status}</span></div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Referral History */}
              <Card className="bg-zinc-800/50 border-zinc-700">
                <CardContent className="p-4">
                  <h4 className="font-medium text-white mb-2">Referral History ({auditData.referral_history?.length || 0})</h4>
                  <ScrollArea className="h-[150px]">
                    {auditData.referral_history?.length > 0 ? (
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-zinc-500">
                            <th className="text-left p-1">Referral</th>
                            <th className="text-left p-1">Status</th>
                            <th className="text-left p-1">Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {auditData.referral_history.map((ref, idx) => (
                            <tr key={idx} className="text-zinc-400">
                              <td className="p-1">{ref.referred_email}</td>
                              <td className="p-1">{ref.status}</td>
                              <td className="p-1">{new Date(ref.created_at).toLocaleDateString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="text-zinc-500">No referrals yet</p>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>
              
              {/* Internal Notes */}
              <Card className="bg-zinc-800/50 border-zinc-700">
                <CardContent className="p-4">
                  <h4 className="font-medium text-white mb-2">Internal Notes</h4>
                  <ScrollArea className="h-[100px] mb-3">
                    {auditData.internal_notes?.length > 0 ? (
                      <div className="space-y-2">
                        {auditData.internal_notes.map((note, idx) => (
                          <div key={idx} className="p-2 bg-zinc-900 rounded text-sm">
                            <p className="text-white">{note.note}</p>
                            <p className="text-xs text-zinc-500 mt-1">
                              By {note.created_by_name || note.created_by} • {new Date(note.created_at).toLocaleString()}
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-zinc-500">No internal notes</p>
                    )}
                  </ScrollArea>
                  
                  <div className="flex gap-2">
                    <Input
                      value={internalNote}
                      onChange={(e) => setInternalNote(e.target.value)}
                      placeholder="Add internal note..."
                      className="bg-zinc-800 border-zinc-700"
                    />
                    <Button onClick={handleAddNote} disabled={!internalNote.trim()}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAuditDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
